// v16.17 Phase C.38 — Multi-role switch buttons.
// Hiển thị nút "Chuyển sang [role]" cho MỌI role user có, TRỪ role hiện tại.
// Không giấu vào menu — luôn visible ở header dashboard.
import { Link, useNavigate } from "react-router-dom";
import {
  Crown,
  User,
  IdentificationCard,
  ShieldCheck,
} from "@phosphor-icons/react";
import { firstAccessibleAdminPath } from "../../lib/adminMenuCatalog";

const ROLE_META = {
  athlete: {
    label: "Vận động viên",
    to: "/athlete/home",
    icon: User,
    tone: "from-sky-500/15 to-cyan-500/15 border-sky-400/35 text-sky-200 hover:from-sky-500/25 hover:to-cyan-500/25",
    testid: "role-switch-athlete-btn",
  },
  referee: {
    label: "Trọng tài",
    to: "/referee",
    icon: IdentificationCard,
    tone: "from-emerald-500/15 to-teal-500/15 border-emerald-400/35 text-emerald-200 hover:from-emerald-500/25 hover:to-teal-500/25",
    testid: "role-switch-referee-btn",
  },
  organizer: {
    label: "Ban tổ chức",
    // Path resolve động qua firstAccessibleAdminPath.
    to: null,
    icon: Crown,
    tone: "from-amber-500/15 to-orange-500/15 border-amber-400/35 text-amber-200 hover:from-amber-500/25 hover:to-orange-500/25",
    testid: "role-switch-organizer-btn",
  },
  admin: {
    label: "Quản trị",
    to: null,
    icon: ShieldCheck,
    tone: "from-indigo-500/15 to-blue-500/15 border-indigo-400/35 text-indigo-200 hover:from-indigo-500/25 hover:to-blue-500/25",
    testid: "role-switch-admin-btn",
  },
  super_admin: {
    label: "Super Admin",
    to: null,
    icon: ShieldCheck,
    tone: "from-purple-500/15 to-fuchsia-500/15 border-purple-400/35 text-purple-200 hover:from-purple-500/25 hover:to-fuchsia-500/25",
    testid: "role-switch-super-admin-btn",
  },
};

// Order hiển thị: athlete → referee → organizer → admin → super_admin.
const ROLE_ORDER = ["athlete", "referee", "organizer", "admin", "super_admin"];

/**
 * Props:
 *   currentRole  — role đang đứng (không hiện nút cho role này).
 *                  Nếu currentRole thuộc admin-tier (organizer/admin/super_admin)
 *                  thì cũng ẩn các role admin-tier khác (cùng tuyến admin panel).
 *   user         — user object để check hasRole.
 *   compact      — chỉ show icon (mobile).
 *   short        — show label ngắn (bỏ prefix "Chuyển sang").
 */
export default function RoleSwitchButtons({ currentRole, user, compact = false, short = false }) {
  const navigate = useNavigate();
  if (!user) return null;
  const userRoles = new Set(Array.isArray(user.roles) ? user.roles : []);
  // Nếu current thuộc admin-tier → skip toàn bộ role admin-tier khác vì
  // đang ở khu admin rồi. Chỉ show role KHÁC TUYẾN (athlete/referee).
  const ADMIN_TIER = new Set(["organizer", "admin", "super_admin"]);
  const isAdminTier = ADMIN_TIER.has(currentRole);

  const buttons = ROLE_ORDER.filter((r) => {
    if (r === currentRole) return false;
    if (isAdminTier && ADMIN_TIER.has(r)) return false;
    // Admin-tier LUÔN được xem giao diện VĐV (giống nav "Chuyển sang VĐV"),
    // kể cả khi tài khoản không giữ role athlete thực sự.
    if (r === "athlete" && isAdminTier) return true;
    if (!userRoles.has(r)) return false;
    return true;
  });

  if (buttons.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="role-switch-group">
      {buttons.map((r) => {
        const meta = ROLE_META[r];
        const Icon = meta.icon;
        // Admin → VĐV: dùng luồng overlay (set flag + về "/") giống nav,
        // thay vì điều hướng thẳng /athlete/home.
        const adminToAthlete = r === "athlete" && isAdminTier;
        if (adminToAthlete) {
          return (
            <button
              key={r}
              type="button"
              onClick={() => {
                localStorage.setItem("pl_admin_overlay_v1", "1");
                navigate("/");
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r ${meta.tone} border text-xs font-bold`}
              data-testid={meta.testid}
              title={`Chuyển sang ${meta.label}`}
            >
              <Icon size={12} weight="fill" />
              {!compact && <span>{short ? meta.label : `Chuyển sang ${meta.label}`}</span>}
            </button>
          );
        }
        const to = meta.to || firstAccessibleAdminPath(user) || "/admin/tournaments";
        return (
          <Link
            key={r}
            to={to}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r ${meta.tone} border text-xs font-bold`}
            data-testid={meta.testid}
            title={`Chuyển sang ${meta.label}`}
          >
            <Icon size={12} weight="fill" />
            {!compact && <span>{short ? meta.label : `Chuyển sang ${meta.label}`}</span>}
          </Link>
        );
      })}
    </div>
  );
}
